import { Plus, Target, Calendar, Layers, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";

interface ProjectOverviewProps {
  serviceOrder: ServiceOrderDto;
  totalRequests: number;
  pendingCount: number;
  onNewRequest: () => void;
}

export function ProjectOverview({
  serviceOrder,
  totalRequests,
  pendingCount,
  onNewRequest,
}: ProjectOverviewProps) {
  return (
    <div className="glass-card rounded-2xl p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Layers className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-foreground mb-1">
            {serviceOrder.project.name}
          </h2>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {serviceOrder.project.scope.description}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Deliverables
            </span>
          </div>
          <div className="space-y-1.5">
            {serviceOrder.project.deliverables.slice(0, 3).map((d) => (
              <div key={d.id} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                <span className="text-sm text-foreground truncate">
                  {d.title}
                </span>
              </div>
            ))}
            {serviceOrder.project.deliverables.length > 3 && (
              <p className="text-xs text-muted-foreground pl-3.5">
                +{serviceOrder.project.deliverables.length - 3} more
              </p>
            )}
          </div>
        </div>

        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <Flag className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Milestones
            </span>
          </div>
          <div className="space-y-1.5">
            {serviceOrder.project.milestones.length > 0 ? (
              serviceOrder.project.milestones.slice(0, 3).map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60" />
                  <span className="text-sm text-foreground truncate">
                    {m.title}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                No milestones defined
              </p>
            )}
            {serviceOrder.project.milestones.length > 3 && (
              <p className="text-xs text-muted-foreground pl-3.5">
                +{serviceOrder.project.milestones.length - 3} more
              </p>
            )}
          </div>
        </div>

        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Timeline
            </span>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-muted-foreground">Start</p>
              <p className="text-sm text-foreground">
                {new Date(
                  serviceOrder.project.timeline.startDate
                ).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">End</p>
              <p className="text-sm text-foreground">
                {new Date(
                  serviceOrder.project.timeline.endDate
                ).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/5">
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <span>{totalRequests} total requests</span>
          <span>{pendingCount} pending</span>
        </div>
        <Button onClick={onNewRequest} className="gap-2">
          <Plus className="w-4 h-4" />
          New Request
        </Button>
      </div>
    </div>
  );
}

